import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { api, apiErrorMessage } from '../../services/api'
import { Button, ErrorText, Field, Select, Textarea } from '../../components/ui'
import { Icon } from '../../components/ui/icons'
import { formatTime, NO_ATTENDANCE_REASON_LABELS } from '../../utils/format'
import type { Appointment, AppointmentStatus, NoAttendanceReason } from '../../types/api'

/**
 * Tránsito del paciente en la sede (H1), inspirado en la "Lista de Citas por
 * Sesión" del HIS de referencia: marcas Llegó / Pasó a box / Atendido con su
 * hora, llamado a box con contador y motivo de no atención.
 *
 * Las marcas son acumulativas y el backend estampa las anteriores si se salta
 * una, así que aquí basta con enviar la transición correspondiente.
 */

interface Mark {
  label: string
  short: string
  status: AppointmentStatus
  at: (a: Appointment) => string | null | undefined
  /** Estados desde los que la marca se puede poner. */
  from: AppointmentStatus[]
}

const MARKS: Mark[] = [
  { label: 'Llegó', short: 'Llegó', status: 'ARRIVED', at: (a) => a.arrivedAt, from: ['SCHEDULED', 'CONFIRMED'] },
  { label: 'Pasó a box', short: 'Box', status: 'IN_ROOM', at: (a) => a.inRoomAt, from: ['SCHEDULED', 'CONFIRMED', 'ARRIVED'] },
  { label: 'Atendido', short: 'Atendido', status: 'ATTENDED', at: (a) => a.attendedAt, from: ['CONFIRMED', 'ARRIVED', 'IN_ROOM'] },
]

/** Estados en los que el paciente está (o debería estar) en la sede. */
const IN_TRANSIT: AppointmentStatus[] = ['SCHEDULED', 'CONFIRMED', 'ARRIVED', 'IN_ROOM']

export function TransitControls({ appointment, compact = false }: { appointment: Appointment; compact?: boolean }) {
  const queryClient = useQueryClient()
  const [error, setError] = useState('')
  const [showNoAttendance, setShowNoAttendance] = useState(false)
  const [reason, setReason] = useState<NoAttendanceReason>('PATIENT_NO_SHOW')
  const [note, setNote] = useState('')

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['appointments'] })
    queryClient.invalidateQueries({ queryKey: ['appointment', appointment.id] })
    queryClient.invalidateQueries({ queryKey: ['charges'] })
    setError('')
  }

  const mark = useMutation({
    mutationFn: async (status: AppointmentStatus) =>
      (await api.post(`/appointments/${appointment.id}/transition`, { status })).data,
    onSuccess: refresh,
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const call = useMutation({
    mutationFn: async () => (await api.post(`/appointments/${appointment.id}/call`, {})).data,
    onSuccess: refresh,
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const noAttendance = useMutation({
    mutationFn: async () =>
      (await api.post(`/appointments/${appointment.id}/no-attendance`, { reason, note: note || null })).data,
    onSuccess: () => {
      setShowNoAttendance(false)
      setNote('')
      refresh()
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const busy = mark.isPending || call.isPending || noAttendance.isPending
  const canCall = IN_TRANSIT.includes(appointment.status)

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {MARKS.map((m) => {
          const at = m.at(appointment)
          const done = Boolean(at)
          const available = !done && m.from.includes(appointment.status)
          return (
            <button
              key={m.status}
              type="button"
              disabled={busy || !available}
              title={
                done
                  ? `${m.label} a las ${formatTime(at!)}`
                  : available
                    ? `Marcar «${m.label}»`
                    : `No disponible con la cita en este estado`
              }
              onClick={() => mark.mutate(m.status)}
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                done
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                  : available
                    ? 'border-steel-200 bg-white text-steel-700 hover:border-brand-400 hover:bg-soft'
                    : 'cursor-not-allowed border-steel-100 bg-steel-50 text-steel-400',
              )}
            >
              <Icon name={done ? 'check' : 'clock'} className="h-3.5 w-3.5" />
              {compact ? m.short : m.label}
              {done && <span className="tabular-nums text-[11px] opacity-80">{formatTime(at!)}</span>}
            </button>
          )
        })}

        {canCall && (
          <button
            type="button"
            disabled={busy}
            onClick={() => call.mutate()}
            title="Llamar al paciente a box"
            className="inline-flex items-center gap-1.5 rounded-full border border-brand-300 bg-white px-2.5 py-1 text-xs font-semibold text-brand-700 transition-colors hover:bg-soft disabled:opacity-60"
          >
            <Icon name="callPatient" className="h-3.5 w-3.5" />
            Llamar
            {appointment.callCount > 0 && (
              <span className="rounded-full bg-brand-600 px-1.5 text-[11px] font-bold text-white">{appointment.callCount}</span>
            )}
          </button>
        )}

        {IN_TRANSIT.includes(appointment.status) && !showNoAttendance && (
          <button
            type="button"
            disabled={busy}
            onClick={() => setShowNoAttendance(true)}
            className="inline-flex items-center gap-1.5 rounded-full border border-steel-200 bg-white px-2.5 py-1 text-xs font-medium text-steel-600 transition-colors hover:border-amber-400 hover:text-amber-700"
          >
            <Icon name="noShow" className="h-3.5 w-3.5" />
            No atendido
          </button>
        )}
      </div>

      {appointment.lastCalledAt && (
        <p className="text-xs text-steel-500">
          Último llamado {formatTime(appointment.lastCalledAt)}
          {appointment.lastCalledBox ? ` · ${appointment.lastCalledBox.name}` : ''} · {appointment.callCount}{' '}
          {appointment.callCount === 1 ? 'llamado' : 'llamados'}
        </p>
      )}

      {appointment.noAttendanceReason && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
          No atendido: {NO_ATTENDANCE_REASON_LABELS[appointment.noAttendanceReason]}
          {appointment.noAttendanceNote ? ` — ${appointment.noAttendanceNote}` : ''}
        </p>
      )}

      {showNoAttendance && (
        <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <Field label="Motivo de no atención">
            <Select value={reason} onChange={(e) => setReason(e.target.value as NoAttendanceReason)}>
              {Object.entries(NO_ATTENDANCE_REASON_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Select>
          </Field>
          <Field label="Detalle (opcional)">
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
          <div className="flex gap-2">
            <Button variant="primary" disabled={busy} onClick={() => noAttendance.mutate()}>
              Registrar no atención
            </Button>
            <Button variant="ghost" onClick={() => setShowNoAttendance(false)}>Volver</Button>
          </div>
        </div>
      )}

      <ErrorText>{error}</ErrorText>
    </div>
  )
}
